import { Core, Decorative } from "@faire/design-tokens";
import { logAssert } from "@faire/web--source/common/logging";
import { NonBreaking } from "@faire/web--source/common/string";
import { Button } from "@faire/web--source/slate/Button";
import { Weight } from "@faire/web--source/slate/Font/utils";
import { Close } from "@faire/web--source/slate/icons/foundation/Close";
import { Row } from "@faire/web--source/slate/Layout";
import { getSpacing } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { EmbeddedLink } from "@faire/web--source/ui/EmbeddedLink";
import { Link } from "@faire/web--source/ui/LinkAnchor";
import { Route } from "@faire/web--source/ui/routing/Route";
import { Switch } from "@faire/web--source/ui/routing/Switch";
import { AlertBannerType } from "@faire/web-api--source/indigofair/data/AlertBannerType";
import { IAlertBanner } from "@faire/web-api--source/indigofair/data/IAlertBanner";
import kebabCase from "lodash/kebabCase";
import * as React from "react";
import styled from "styled-components";

import { trackRetailerEvent } from "@faire/retailer/lib/trackRetailerEvent";

import { FormattedText } from "./FormattedText";

interface IPropTypes {
  value: IAlertBanner & { onClick?: () => void };
  onDismiss: (type?: keyof typeof AlertBannerType) => void;
  forwardRef?: React.Ref<HTMLDivElement>;
  clientInitiated?: boolean;
}

class IconBanner extends React.PureComponent<IPropTypes> {
  render() {
    return (
      <Switch>
        {["/brand-invoice"].map((path) => (
          <Route key={path} path={path} />
        ))}
        <Route>{this.renderBanner()}</Route>
      </Switch>
    );
  }

  private renderBanner = () => {
    const { value, forwardRef } = this.props;

    const backgroundColor =
      value.background_color_hex && value.background_color_hex.startsWith("#")
        ? value.background_color_hex
        : `#${value.background_color_hex ?? "333"}`;

    const textColor =
      value.text_color_hex && value.text_color_hex.startsWith("#")
        ? value.text_color_hex
        : `#${value.text_color_hex ?? "fff"}`;

    const internalLinkOpensNewTab =
      value.type === "UPFUNNEL_IDV_REQUEST" || value.type === "IDV_REQUEST";

    const renderCtaLink = () => {
      const learnMoreEntity = value.entities?.["learn_more"];
      const route = learnMoreEntity?.["route"];
      const ctaText = learnMoreEntity?.["text"];

      if (!route || !ctaText) {
        return null;
      }

      return (
        <NowrapSpan>
          <StyledButton variant="tertiary" linkTo={route}>
            <Text>{ctaText}</Text>
          </StyledButton>
        </NowrapSpan>
      );
    };

    return (
      <IconBannerWrapper
        ref={forwardRef}
        data-test-id={`${
          value.type ? kebabCase(value.type) : "client-side-alert"
        }-banner`}
        $background={backgroundColor}
        $textColor={textColor}
      >
        {value.icon_url ? (
          <IconImg
            src={value.icon_url}
            $small={
              value.type === AlertBannerType.INSIDER_THRESHOLD_BANNER ||
              value.type === AlertBannerType.INVOICES_OVERDUE
            }
          />
        ) : null}
        <MessageWrapper>
          {value.type === AlertBannerType.INSIDER_THRESHOLD_BANNER ? (
            <InsiderPrefixWrapper>{value.title}</InsiderPrefixWrapper>
          ) : (
            <Title>{value.title}</Title>
          )}

          {value.title && value.message ? NonBreaking.space : null}
          <Text>
            {value.message
              ? value.message
                  .split(/({{)|(}})/)
                  .filter((val) => val !== undefined)
                  .map((val, i, arr) => {
                    const entity = value.entities[val];
                    if (entity && arr[i - 1] === "{{" && arr[i + 1] === "}}") {
                      if (entity.route && entity.route.startsWith("http")) {
                        return (
                          <NowrapSpan key={i}>
                            <EmbeddedLink
                              linkTo={entity.route}
                              target="_blank"
                              style={{ textDecoration: "none" }}
                              onClick={this.onClick(val)}
                            >
                              {entity.text}
                            </EmbeddedLink>
                          </NowrapSpan>
                        );
                      }
                      return (
                        <NowrapSpan key={i}>
                          <Link
                            to={entity.route!}
                            onClick={this.onClick(val)}
                            target={
                              internalLinkOpensNewTab ? "_blank" : undefined
                            }
                          >
                            {entity.text}
                          </Link>
                        </NowrapSpan>
                      );
                    }
                    if (val !== "{{" && val !== "}}") {
                      return <FormattedText key={i} text={val} />;
                    }
                    return null;
                  })
              : null}
            {!value.message?.includes("learn_more") ? renderCtaLink() : null}
          </Text>
        </MessageWrapper>
        {value.dismissable ? (
          <CloseButton
            onClick={this.onClose}
            data-test-id="icon-banner-dismiss-button"
          >
            <Close fontSize="16px" color={textColor} />
          </CloseButton>
        ) : null}
      </IconBannerWrapper>
    );
  };

  private onClose = () => {
    const { clientInitiated, onDismiss, value } = this.props;
    if (clientInitiated) {
      onDismiss();
    } else {
      logAssert(!!value?.type, "value must exist on AlertBanner");
      trackRetailerEvent(`alert-banner/${value.type.toLowerCase()}/dismiss`);
      onDismiss(value.type);
    }
  };

  private onClick = (entityKey?: string) => {
    return () => {
      const { clientInitiated, value } = this.props;
      if (!clientInitiated) {
        logAssert(!!value?.type, "value must exist on AlertBanner");
        if (entityKey) {
          trackRetailerEvent(
            `alert-banner/${value.type.toLowerCase()}/${entityKey.toLowerCase()}`
          );
        }
      }
      if (value.onClick) {
        value.onClick();
      }
    };
  };
}

/**
 * @trackcomponent
 * @supersededBy "@faire/web/slate/IconButton/IconButton#IconButton"
 */
const CloseButton = styled.button`
  cursor: pointer;
  outline: 0;
  background: transparent;
  border: 0;
  padding: 10px;
  line-height: 0; // otherwise button has height

  ${responsiveMediaQueries.tabletAndAbove} {
    position: absolute;
    top: 0px;
    right: 10px;
  }
`;

const IconBannerWrapper = styled(Row).attrs({
  justify: "center",
  align: {
    mobileAndAbove: "flex-start",
    tabletAndAbove: "center",
  },
})<{
  $background: string | undefined;
  $textColor: string | undefined;
}>`
  position: relative;
  background: ${(props) => props.$background ?? Core.surface.default};
  color: ${(props) => props.$textColor ?? Core.text.inverse};
  width: 100%;
  z-index: 10;
  padding: 10px 8px;

  column-gap: 4px;

  ${responsiveMediaQueries.tabletAndAbove} {
    padding: 5px 32px;
    min-height: 34px;
  }
`;

const IconImg = styled.img<{
  $small: boolean;
}>`
  width: ${(props) => (props.$small ? "16px" : "24px")};
  margin-top: ${(props) => (props.$small ? "3px" : "0px")};
  height: ${(props) => (props.$small ? "16px" : "fit-content")};
  flex-shrink: 0;
`;

const InsiderPrefixWrapper = styled(Typography).attrs({
  color: Decorative.icon.teal,
  variant: "paragraphSansMedium",
})`
  &&& {
    display: inline;
    margin-right: 7px;
  }
`;

const MessageWrapper = styled.div`
  margin: ${getSpacing(0, 4)};

  ${responsiveMediaQueries.mobile} {
    line-height: 15px;
  }
`;

const Title = styled(Typography).attrs({
  variant: "paragraphSansMedium",
  color: "inherit",
})`
  display: inline;
`;

const Text = styled(Typography).attrs({
  color: "inherit",
})`
  display: inline;

  a {
    font-weight: ${Weight.medium};
    text-decoration: underline;
    color: inherit;
  }

  b {
    font-weight: ${Weight.medium};
  }
`;

const StyledButton = styled(Button)`
  margin: 0 4px;
`;

const NowrapSpan = styled.span`
  white-space: nowrap;
`;

export const IconBannerForwardRef = React.forwardRef<
  HTMLDivElement,
  IPropTypes
>((props: IPropTypes, ref) => <IconBanner {...props} forwardRef={ref} />);
